import React, { useCallback, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ControlPopupPresence from './ControlPopupPresence';

export type EndnotePopupStatus = 'loading' | 'ready' | 'error' | 'not-found';

interface EndnotePopupProps {
  isOpen: boolean;
  status: EndnotePopupStatus;
  noteElement: HTMLElement | null;
  noteNumber: string | null;
  anchorRect: DOMRect | null;
  portalTarget: HTMLElement | null;
  onClose: () => void;
  onRetry: () => void;
  titleId: string;
}

const POPUP_WIDTH_ESTIMATE = 320;
const POPUP_HEIGHT_ESTIMATE = 200;
const VIEWPORT_MARGIN = 16;

// #reader-popup-root is `position: fixed; inset: 0`, so its containing block
// is the viewport -- anchorRect (from getBoundingClientRect()) can be used
// directly without translating through any scroll-container/shadow-DOM offsets.
const computePosition = (anchorRect: DOMRect | null): React.CSSProperties => {
  if (!anchorRect) {
    return { visibility: 'hidden' };
  }
  const maxLeft = window.innerWidth - POPUP_WIDTH_ESTIMATE - VIEWPORT_MARGIN;
  const left = Math.max(VIEWPORT_MARGIN, Math.min(anchorRect.left, Math.max(VIEWPORT_MARGIN, maxLeft)));
  const fitsBelow = anchorRect.bottom + POPUP_HEIGHT_ESTIMATE + VIEWPORT_MARGIN <= window.innerHeight;
  const top = fitsBelow
    ? anchorRect.bottom + 8
    : Math.max(VIEWPORT_MARGIN, anchorRect.top - POPUP_HEIGHT_ESTIMATE - 8);
  return { top, left };
};

const EndnotePopup: React.FC<EndnotePopupProps> = ({
  isOpen,
  status,
  noteElement,
  noteNumber,
  anchorRect,
  portalTarget,
  onClose,
  onRetry,
  titleId,
}) => {
  const setDialogRef = useCallback((dialog: HTMLDivElement | null) => {
    if (dialog && isOpen) dialog.focus();
  }, [isOpen]);
  // A plain useRef wouldn't retrigger the population effect below when the
  // container mounts on its own (e.g. reopening after a full close, where
  // ControlPopupPresence's isOpen->isRendered flip lands in a separate commit
  // from the status/noteElement update that arrives with it) -- state ensures
  // the effect also reruns when the DOM node itself becomes available, not
  // only when status/noteElement change.
  const [contentContainer, setContentContainer] = useState<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!contentContainer) {
      return;
    }
    if (status !== 'ready' || !noteElement) {
      contentContainer.replaceChildren();
      return;
    }
    contentContainer.replaceChildren(noteElement.cloneNode(true));
  }, [contentContainer, status, noteElement]);

  if (!portalTarget) {
    return null;
  }

  return createPortal(
    <ControlPopupPresence isOpen={isOpen}>
      {(animationState) => (
        <div
          ref={setDialogRef}
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          tabIndex={-1}
          className={`reader-endnote-popup reader-endnote-popup-${animationState}`}
          style={computePosition(anchorRect)}
        >
          <div className="reader-endnote-popup-header">
            <span id={titleId} className="reader-endnote-popup-title">
              {noteNumber ? `Note ${noteNumber}` : 'Note'}
            </span>
            <button
              type="button"
              aria-label="Close note"
              className="reader-endnote-popup-close"
              onClick={onClose}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
          <div className="reader-endnote-popup-body">
            {status === 'loading' && (
              <div className="reader-endnote-popup-loading" role="status" aria-live="polite">
                <span className="reader-endnote-popup-spinner" aria-hidden="true" />
                Loading note…
              </div>
            )}
            {status === 'error' && (
              <div className="reader-endnote-popup-error">
                <p>Couldn&apos;t load notes.</p>
                <button type="button" className="reader-endnote-popup-retry" onClick={onRetry}>Retry</button>
              </div>
            )}
            {status === 'not-found' && <p className="reader-endnote-popup-status">Note not found.</p>}
            {/* Always mounted (not gated on status) -- content population is
                driven entirely by the layout effect above. */}
            <div
              ref={setContentContainer}
              className="reader-endnote-popup-content"
              style={{ display: status === 'ready' ? undefined : 'none' }}
            />
          </div>
        </div>
      )}
    </ControlPopupPresence>,
    portalTarget,
  );
};

export default EndnotePopup;
